import { useCallback, useEffect, useState } from 'react'
import type { CenarioExtracao } from '../dominio/didatico/cenariosExtracao'
import {
  cenarioAutoralAPartirDe,
  novoCenarioAutoral,
  type CenarioAutoral,
} from '../dominio/didatico/cenariosAutorais'
import {
  desserializarCenarioAutoral,
  serializarCenarioAutoralAluno,
  serializarCenarioAutoralProfessor,
} from '../dominio/didatico/cenariosAutoraisSerializacao'

const CHAVE_CENARIOS_AUTORAIS_LOCAL = 'csv-lab-cenarios-autorais-v1'

/**
 * Cada item já é o formato completo de `serializarCenarioAutoralProfessor`
 * (com a própria `versao`) — um item corrompido não derruba a lista inteira,
 * só é descartado individualmente.
 */
function carregarCenariosAutoraisLocal(): readonly CenarioAutoral[] {
  if (typeof window === 'undefined') return []
  try {
    const salvo = window.localStorage.getItem(CHAVE_CENARIOS_AUTORAIS_LOCAL)
    if (!salvo) return []
    const bruto = JSON.parse(salvo) as unknown
    if (!Array.isArray(bruto)) return []
    const cenarios: CenarioAutoral[] = []
    for (const item of bruto) {
      try {
        cenarios.push(desserializarCenarioAutoral(JSON.stringify(item)))
      } catch {
        // um cenário corrompido não impede os outros de carregar
      }
    }
    return cenarios
  } catch {
    return []
  }
}

const idNovo = (): string => `autoral-${Date.now()}-${Math.round(Math.random() * 1e4)}`

export interface ControleCenariosAutorais {
  lista: readonly CenarioAutoral[]
  criar: () => string
  criarAPartirDe: (cenarioBase: CenarioExtracao) => string
  atualizar: (cenario: CenarioAutoral) => void
  remover: (id: string) => void
  duplicar: (id: string) => string | null
  exportarProfessor: (id: string) => string | null
  exportarAluno: (id: string) => string | null
  importar: (json: string) => { sucesso: boolean; mensagem: string }
}

export function useCenariosAutorais(): ControleCenariosAutorais {
  const [lista, setLista] = useState(carregarCenariosAutoraisLocal)

  useEffect(() => {
    const agendamento = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          CHAVE_CENARIOS_AUTORAIS_LOCAL,
          JSON.stringify(lista.map((cenario) => JSON.parse(serializarCenarioAutoralProfessor(cenario)))),
        )
      } catch {
        // sem persistência; a lista continua funcionando na sessão atual
      }
    }, 250)
    return () => window.clearTimeout(agendamento)
  }, [lista])

  const criar = useCallback(() => {
    const id = idNovo()
    setLista((atual) => [...atual, novoCenarioAutoral(id)])
    return id
  }, [])

  const criarAPartirDe = useCallback((cenarioBase: CenarioExtracao) => {
    const id = idNovo()
    setLista((atual) => [...atual, cenarioAutoralAPartirDe(cenarioBase, id)])
    return id
  }, [])

  const atualizar = useCallback((cenario: CenarioAutoral) => {
    setLista((atual) => atual.map((item) => (item.id === cenario.id ? cenario : item)))
  }, [])

  const remover = useCallback((id: string) => {
    setLista((atual) => atual.filter((item) => item.id !== id))
  }, [])

  const duplicar = useCallback((id: string) => {
    let novoId: string | null = null
    setLista((atual) => {
      const original = atual.find((item) => item.id === id)
      if (!original) return atual
      novoId = idNovo()
      const copia: CenarioAutoral = {
        ...original,
        id: novoId,
        cenario: { ...original.cenario, id: novoId, nome: `${original.cenario.nome} (cópia)` },
        criadoEm: new Date().toISOString(),
      }
      return [...atual, copia]
    })
    return novoId
  }, [])

  const exportarProfessor = useCallback(
    (id: string) => {
      const cenario = lista.find((item) => item.id === id)
      return cenario ? serializarCenarioAutoralProfessor(cenario) : null
    },
    [lista],
  )

  const exportarAluno = useCallback(
    (id: string) => {
      const cenario = lista.find((item) => item.id === id)
      return cenario ? serializarCenarioAutoralAluno(cenario) : null
    },
    [lista],
  )

  const importar = useCallback((json: string) => {
    try {
      const cenario = desserializarCenarioAutoral(json)
      setLista((atual) => {
        // um id importado que já existe localmente ganha um id novo — nunca sobrescreve em silêncio
        const colide = atual.some((item) => item.id === cenario.id)
        const final = colide ? { ...cenario, id: idNovo() } : cenario
        return [...atual, { ...final, cenario: { ...final.cenario, id: final.id } }]
      })
      return { sucesso: true, mensagem: `Cenário "${cenario.cenario.nome}" importado.` }
    } catch (erro) {
      return { sucesso: false, mensagem: erro instanceof Error ? erro.message : 'Arquivo inválido.' }
    }
  }, [])

  return { lista, criar, criarAPartirDe, atualizar, remover, duplicar, exportarProfessor, exportarAluno, importar }
}
